// src/dev/OnboardingInspectorContext.tsx
// Context for the Onboarding Inspector testing suite
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useUser } from "@clerk/clerk-react";
import type {
  InspectorOverrides,
  NetworkLogEntry,
  StageOverride,
  RunStateOverride,
  DataOverride,
  JourneyOverride,
  LocalStorageState,
  ClerkMetadataState,
  StorageDiscrepancy,
  SimulationMode,
} from "./types/inspector-types";

// Default override states
const DEFAULT_STAGE_OVERRIDE: StageOverride = {
  enabled: false,
  stage: "START",
};

const DEFAULT_RUN_STATE_OVERRIDE: RunStateOverride = {
  enabled: false,
  runState: "first",
};

const DEFAULT_DATA_OVERRIDE: DataOverride = {
  enabled: false,
  mode: "complete",
};

const DEFAULT_JOURNEY_OVERRIDE: JourneyOverride = {
  enabled: false,
  state: "new",
};

const DEFAULT_OVERRIDES: InspectorOverrides = {
  stage: DEFAULT_STAGE_OVERRIDE,
  runState: DEFAULT_RUN_STATE_OVERRIDE,
  data: DEFAULT_DATA_OVERRIDE,
  journey: DEFAULT_JOURNEY_OVERRIDE,
};

const DEFAULT_SIMULATION_MODE: SimulationMode = {
  enabled: false,
  simulatedEmail: "test-user@example.com",
  interceptedMetadata: {},
  localStorageWrites: {},
};

// Context type
interface OnboardingInspectorContextType {
  // Panel state
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggleOpen: () => void;

  // Force onboarding - bypass redirect to /trends
  forceOnboarding: boolean;
  setForceOnboarding: (enabled: boolean) => void;

  // Simulation mode - pretend to be a new user
  simulationMode: SimulationMode;
  setSimulationEnabled: (enabled: boolean) => void;
  setSimulatedEmail: (email: string) => void;
  interceptClerkWrite: (metadata: Record<string, unknown>) => void;
  clearSimulation: () => void;

  // Network logging
  networkLogs: NetworkLogEntry[];
  addNetworkLog: (entry: Omit<NetworkLogEntry, "id" | "timestamp">) => void;
  updateNetworkLog: (id: string, updates: Partial<NetworkLogEntry>) => void;
  clearNetworkLogs: () => void;

  // Overrides
  overrides: InspectorOverrides;
  setStageOverride: (override: StageOverride) => void;
  setRunStateOverride: (override: RunStateOverride) => void;
  setDataOverride: (override: DataOverride) => void;
  setJourneyOverride: (override: JourneyOverride) => void;
  clearAllOverrides: () => void;

  // Storage inspection
  localStorageState: LocalStorageState;
  clerkMetadataState: ClerkMetadataState;
  discrepancies: StorageDiscrepancy[];
  refreshStorageState: () => void;

  // Export
  exportState: () => string;
}

export const OnboardingInspectorContext =
  createContext<OnboardingInspectorContextType | null>(null);

// Storage keys
const KEYS = {
  inspectorOpen: "dev_inspector_open",
  stageOverride: "dev_onboarding_stage_override",
  runStateOverride: "dev_run_state_override",
  dataOverride: "dev_data_override",
  journeyOverride: "dev_journey_override",
  simulationMode: "dev_simulation_mode",
  simulatedClerkMetadata: "dev_simulated_clerk_metadata",
  forceOnboarding: "dev_force_onboarding",
} as const;

// localStorage keys to inspect
const LS_KEYS_TO_INSPECT = [
  "temp_web_app_url",
  "temp_sheet_url",
  "temp_account_data",
  "onboarding_progress",
  "onboarding_last_step",
];

export function OnboardingInspectorProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user } = useUser();
  const isDevMode = process.env.NODE_ENV === "development";

  // Panel state
  const [isOpen, setIsOpenState] = useState(false);

  // Simulation mode
  const [simulationMode, setSimulationMode] = useState<SimulationMode>(
    DEFAULT_SIMULATION_MODE
  );

  // Network logs
  const [networkLogs, setNetworkLogs] = useState<NetworkLogEntry[]>([]);

  // Overrides
  const [overrides, setOverrides] =
    useState<InspectorOverrides>(DEFAULT_OVERRIDES);

  // Force onboarding mode - bypass redirect to /trends
  const [forceOnboarding, setForceOnboardingState] = useState(false);

  // Storage state
  const [localStorageState, setLocalStorageState] = useState<LocalStorageState>(
    {}
  );
  const [clerkMetadataState, setClerkMetadataState] =
    useState<ClerkMetadataState>({});
  const [discrepancies, setDiscrepancies] = useState<StorageDiscrepancy[]>([]);

  // Load persisted state on mount
  useEffect(() => {
    if (!isDevMode) return;

    // Load panel open state
    const savedOpen = localStorage.getItem(KEYS.inspectorOpen);
    if (savedOpen === "true") {
      setIsOpenState(true);
    }

    // Load force onboarding state
    const savedForceOnboarding = localStorage.getItem(KEYS.forceOnboarding);
    if (savedForceOnboarding === "true") {
      setForceOnboardingState(true);
    }

    // Load simulation mode
    try {
      const savedSimulation = localStorage.getItem(KEYS.simulationMode);
      const savedClerkMetadata = localStorage.getItem(KEYS.simulatedClerkMetadata);
      if (savedSimulation) {
        const parsed = JSON.parse(savedSimulation);
        setSimulationMode({
          ...parsed,
          interceptedMetadata: savedClerkMetadata ? JSON.parse(savedClerkMetadata) : {},
        });
      }
    } catch (e) {
      console.warn("[Inspector] Failed to load simulation mode:", e);
    }

    // Load overrides
    try {
      const savedStage = localStorage.getItem(KEYS.stageOverride);
      const savedRunState = localStorage.getItem(KEYS.runStateOverride);
      const savedData = localStorage.getItem(KEYS.dataOverride);
      const savedJourney = localStorage.getItem(KEYS.journeyOverride);

      setOverrides({
        stage: savedStage ? JSON.parse(savedStage) : DEFAULT_STAGE_OVERRIDE,
        runState: savedRunState
          ? JSON.parse(savedRunState)
          : DEFAULT_RUN_STATE_OVERRIDE,
        data: savedData ? JSON.parse(savedData) : DEFAULT_DATA_OVERRIDE,
        journey: savedJourney
          ? JSON.parse(savedJourney)
          : DEFAULT_JOURNEY_OVERRIDE,
      });
    } catch (e) {
      console.warn("[Inspector] Failed to load saved overrides:", e);
    }
  }, [isDevMode]);

  // Refresh storage state
  const refreshStorageState = useCallback(() => {
    // Read localStorage
    const lsState: LocalStorageState = {};
    for (const key of LS_KEYS_TO_INSPECT) {
      const value = localStorage.getItem(key);
      if (value) {
        (lsState as any)[key] = value;
      }
    }
    setLocalStorageState(lsState);

    // If simulation mode is enabled, show simulated Clerk metadata instead
    if (simulationMode.enabled) {
      const simulated = simulationMode.interceptedMetadata as any;
      setClerkMetadataState({
        onboardingStage: simulated.onboardingStage,
        pendingAccountSetup: simulated.pendingAccountSetup,
        accounts: simulated.accounts,
      });
      setDiscrepancies([]);
      return;
    }

    // Read real Clerk metadata
    const clerkState: ClerkMetadataState = {};
    if (user?.unsafeMetadata) {
      const meta = user.unsafeMetadata as any;
      clerkState.onboardingStage = meta.onboardingStage;
      clerkState.pendingAccountSetup = meta.pendingAccountSetup;
      clerkState.accounts = meta.accounts?.map((acc: any) => ({
        accountCID: acc.accountCID,
        name: acc.name,
        webAppUrl: acc.webAppUrl,
      }));
    }
    setClerkMetadataState(clerkState);

    // Detect discrepancies
    const newDiscrepancies: StorageDiscrepancy[] = [];

    if (
      lsState.temp_web_app_url &&
      !clerkState.pendingAccountSetup?.webAppUrl &&
      !clerkState.accounts?.some((a) => a.webAppUrl)
    ) {
      newDiscrepancies.push({
        field: "webAppUrl",
        localStorage: lsState.temp_web_app_url,
        clerk: undefined,
        message:
          "Web App URL in localStorage but not in Clerk - data may not transfer on signup",
      });
    }

    if (
      lsState.temp_sheet_url &&
      !clerkState.pendingAccountSetup?.sheetUrl
    ) {
      newDiscrepancies.push({
        field: "sheetUrl",
        localStorage: lsState.temp_sheet_url,
        clerk: undefined,
        message:
          "Sheet URL in localStorage but not in Clerk - data may not transfer on signup",
      });
    }

    setDiscrepancies(newDiscrepancies);
  }, [user, simulationMode.enabled, simulationMode.interceptedMetadata]);

  // Refresh storage state when panel opens or user changes
  useEffect(() => {
    if (isOpen) {
      refreshStorageState();
    }
  }, [isOpen, user, refreshStorageState]);

  // Panel controls
  const setIsOpen = useCallback((open: boolean) => {
    setIsOpenState(open);
    localStorage.setItem(KEYS.inspectorOpen, String(open));
  }, []);

  const toggleOpen = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen, setIsOpen]);

  // Force onboarding control
  const setForceOnboarding = useCallback((enabled: boolean) => {
    setForceOnboardingState(enabled);
    localStorage.setItem(KEYS.forceOnboarding, String(enabled));
  }, []);

  // Simulation mode controls
  const setSimulationEnabled = useCallback((enabled: boolean) => {
    setSimulationMode((prev) => {
      const updated = { ...prev, enabled };
      // Clear intercepted metadata when disabling
      if (!enabled) {
        updated.interceptedMetadata = {};
        updated.localStorageWrites = {};
        localStorage.removeItem(KEYS.simulatedClerkMetadata);
      }
      localStorage.setItem(KEYS.simulationMode, JSON.stringify(updated));
      return updated;
    });
  }, []);

  const setSimulatedEmail = useCallback((email: string) => {
    setSimulationMode((prev) => {
      const updated = { ...prev, simulatedEmail: email };
      localStorage.setItem(KEYS.simulationMode, JSON.stringify(updated));
      return updated;
    });
  }, []);

  // Called by other contexts when they would normally write to Clerk
  const interceptClerkWrite = useCallback((metadata: Record<string, unknown>) => {
    setSimulationMode((prev) => {
      const updated = {
        ...prev,
        interceptedMetadata: {
          ...prev.interceptedMetadata,
          ...metadata,
        },
      };
      localStorage.setItem(KEYS.simulatedClerkMetadata, JSON.stringify(updated.interceptedMetadata));
      return updated;
    });
    // Also refresh the display
    setTimeout(refreshStorageState, 100);
  }, [refreshStorageState]);

  const clearSimulation = useCallback(() => {
    setSimulationMode(DEFAULT_SIMULATION_MODE);
    localStorage.removeItem(KEYS.simulationMode);
    localStorage.removeItem(KEYS.simulatedClerkMetadata);
    // Clear simulation-related localStorage
    LS_KEYS_TO_INSPECT.forEach((key) => localStorage.removeItem(key));
    refreshStorageState();
  }, [refreshStorageState]);

  // Network logging
  const addNetworkLog = useCallback(
    (entry: Omit<NetworkLogEntry, "id" | "timestamp">) => {
      const newEntry: NetworkLogEntry = {
        ...entry,
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        timestamp: Date.now(),
      };
      setNetworkLogs((prev) => [...prev, newEntry]);
      return newEntry.id;
    },
    []
  );

  const updateNetworkLog = useCallback(
    (id: string, updates: Partial<NetworkLogEntry>) => {
      setNetworkLogs((prev) =>
        prev.map((log) => (log.id === id ? { ...log, ...updates } : log))
      );
    },
    []
  );

  const clearNetworkLogs = useCallback(() => {
    setNetworkLogs([]);
  }, []);

  // Override setters
  const setStageOverride = useCallback((override: StageOverride) => {
    setOverrides((prev) => ({ ...prev, stage: override }));
    localStorage.setItem(KEYS.stageOverride, JSON.stringify(override));
  }, []);

  const setRunStateOverride = useCallback((override: RunStateOverride) => {
    setOverrides((prev) => ({ ...prev, runState: override }));
    localStorage.setItem(KEYS.runStateOverride, JSON.stringify(override));
  }, []);

  const setDataOverride = useCallback((override: DataOverride) => {
    setOverrides((prev) => ({ ...prev, data: override }));
    localStorage.setItem(KEYS.dataOverride, JSON.stringify(override));
  }, []);

  const setJourneyOverride = useCallback((override: JourneyOverride) => {
    setOverrides((prev) => ({ ...prev, journey: override }));
    localStorage.setItem(KEYS.journeyOverride, JSON.stringify(override));
  }, []);

  const clearAllOverrides = useCallback(() => {
    setOverrides(DEFAULT_OVERRIDES);
    localStorage.removeItem(KEYS.stageOverride);
    localStorage.removeItem(KEYS.runStateOverride);
    localStorage.removeItem(KEYS.dataOverride);
    localStorage.removeItem(KEYS.journeyOverride);
  }, []);

  // Export state
  const exportState = useCallback(() => {
    const state = {
      timestamp: new Date().toISOString(),
      panel: { isOpen },
      simulationMode,
      overrides,
      localStorage: localStorageState,
      clerkMetadata: clerkMetadataState,
      discrepancies,
      networkLogs: networkLogs.map((log) => ({
        ...log,
        timestampISO: new Date(log.timestamp).toISOString(),
      })),
    };
    return JSON.stringify(state, null, 2);
  }, [
    isOpen,
    simulationMode,
    overrides,
    localStorageState,
    clerkMetadataState,
    discrepancies,
    networkLogs,
  ]);

  // Don't render in production
  if (!isDevMode) {
    return <>{children}</>;
  }

  return (
    <OnboardingInspectorContext.Provider
      value={{
        isOpen,
        setIsOpen,
        toggleOpen,
        forceOnboarding,
        setForceOnboarding,
        simulationMode,
        setSimulationEnabled,
        setSimulatedEmail,
        interceptClerkWrite,
        clearSimulation,
        networkLogs,
        addNetworkLog,
        updateNetworkLog,
        clearNetworkLogs,
        overrides,
        setStageOverride,
        setRunStateOverride,
        setDataOverride,
        setJourneyOverride,
        clearAllOverrides,
        localStorageState,
        clerkMetadataState,
        discrepancies,
        refreshStorageState,
        exportState,
      }}
    >
      {children}
    </OnboardingInspectorContext.Provider>
  );
}

export function useOnboardingInspector() {
  const context = useContext(OnboardingInspectorContext);
  if (!context) {
    // Return safe defaults if used outside provider or in production
    return {
      isOpen: false,
      setIsOpen: () => {},
      toggleOpen: () => {},
      forceOnboarding: false,
      setForceOnboarding: () => {},
      simulationMode: DEFAULT_SIMULATION_MODE,
      setSimulationEnabled: () => {},
      setSimulatedEmail: () => {},
      interceptClerkWrite: () => {},
      clearSimulation: () => {},
      networkLogs: [],
      addNetworkLog: () => "",
      updateNetworkLog: () => {},
      clearNetworkLogs: () => {},
      overrides: DEFAULT_OVERRIDES,
      setStageOverride: () => {},
      setRunStateOverride: () => {},
      setDataOverride: () => {},
      setJourneyOverride: () => {},
      clearAllOverrides: () => {},
      localStorageState: {},
      clerkMetadataState: {},
      discrepancies: [],
      refreshStorageState: () => {},
      exportState: () => "{}",
    };
  }
  return context;
}
